import { Center, Group, Pagination, Stack, Title } from "@mantine/core"
import { useDisclosure, useToggle } from "@mantine/hooks"
import {
  IconArrowBigUpLinesFilled,
  IconSortDescending,
  IconSortAscending,
  IconArrowsSort,
} from "@tabler/icons-react"
import { InformationRequestStatus } from "@tfg/db"
import { Button, Card, SectionHeader, Loader, ErrorBox } from "@tfg/ui"
import { useMemo, useState, memo } from "react"
import { useTranslation } from "react-i18next"

import { SupplierResponses } from "@tfg/types/endpoints/supplier"
import { getTraceabilityRequestsQueryParams } from "@tfg/types/schemas/traceabilityRequests"
import { removeUndefined } from "@tfg/types/schemas/utils"

import { queryClient } from "~/backend/queryClient.ts"
import { FindOperationChainsResponseDto } from "~/gen/api"
import { BatchModal } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/BatchModal.tsx"
import { BatchUpdateModalProvider } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/store/BatchModalContext.tsx"
import { SubmitRequestsModal } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/SubmitRequestsModal.tsx"
import { RequestFilters } from "~/pages/SupplierPortal/TraceabilityRequests/components/RequestFilters"
import { TraceabilityRequest } from "~/pages/SupplierPortal/TraceabilityRequests/components/TraceabilityRequest.tsx"
import { useListTraceabilityRequests } from "~/pages/SupplierPortal/TraceabilityRequests/hooks"
import { useGetTraceabilityRequestsIsSortableByOrderDate } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useGetTraceabilityRequestsIsSortableByOrderDate"
import { useGetTracebilityRequestsIsSortableByDueDate } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useGetTracebilityRequestsIsSortableByDueDate"
import { requestsToRequestsWithOp } from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"

const REQUESTS_PER_PAGE = 5

const SORT_ORDER: {
  value: "asc" | "desc" | null
  iconNext: JSX.Element
}[] = [
  {
    value: null,
    iconNext: <IconArrowsSort strokeWidth={1} />,
  },
  {
    value: "asc",
    iconNext: <IconSortAscending strokeWidth={1} />,
  },
  {
    value: "desc",
    iconNext: <IconSortDescending strokeWidth={1} />,
  },
]

export type CheckList = Record<number, boolean>
interface Props {
  status: InformationRequestStatus
  productionSites: SupplierResponses.ProductionSite[] | undefined
  operationChains: FindOperationChainsResponseDto[]
}

export const TraceabilityRequestsList = memo(
  ({ status, productionSites, operationChains }: Props) => {
    const [pagination, setPagination] = useState({
      page: 0,
      pageSize: REQUESTS_PER_PAGE,
    })
    const [orderDateSort, toggleOrderDateSort] = useToggle(SORT_ORDER)
    const [dueDateSort, toggleDueDateSort] = useToggle(SORT_ORDER)
    const [receptionDateSort, toggleReceptionDateSort] = useToggle(SORT_ORDER)
    const [referenceSort, toggleReferenceSort] = useToggle(SORT_ORDER)
    const [clientSort, toggleClientSort] = useToggle(SORT_ORDER)

    const [filters, setFilters] = useState<
      getTraceabilityRequestsQueryParams["filters"]
    >({})
    const {
      data,
      isLoading: isRequestsLoading,
      isError: isRequestsError,
    } = useListTraceabilityRequests({
      filters: removeUndefined({
        status,
        type: filters?.type,
        clientId: filters?.clientId,
        search: filters?.search,
      }),
      pagination,
      sort: removeUndefined({
        orderDate: orderDateSort.value ?? undefined,
        dueDate: dueDateSort.value ?? undefined,
        receptionDate: receptionDateSort.value ?? undefined,
        reference: referenceSort.value ?? undefined,
        client: clientSort.value ?? undefined,
      }),
    })

    const { data: isSortableByDueDate } =
      useGetTracebilityRequestsIsSortableByDueDate({
        filters,
      })

    const { data: isSortableByOrderDate } =
      useGetTraceabilityRequestsIsSortableByOrderDate({
        filters,
      })

    const requests = useMemo(
      () =>
        requestsToRequestsWithOp({
          requests: data?.data ?? [],
          operationChains,
        }),
      [data?.data, operationChains]
    )

    const { t } = useTranslation(["traceability"])

    const [openedSubmit, submitHandlers] = useDisclosure()

    if (isRequestsError) {
      return <ErrorBox title={t("request.errors.generic")} error={null} />
    }

    return (
      <BatchUpdateModalProvider>
        <Stack
          bg="var(--mantine-color-supplier-header-bg)"
          gap="xs"
          w="100%"
          px="var(--tfg-layout-padding-x)"
          py="var(--tfg-layout-padding-y)"
          m={0}
          mih="100vh"
        >
          <Stack
            bg="var(--mantine-color-supplier-header-bg)"
            style={{
              position: "sticky",
              top: `64px`,
              zIndex: "100",
              borderBottom: "1px solid var(--mantine-color-gray-2)",
            }}
            pb="var(--tfg-molecule-padding-y)"
            px="var(--tfg-molecule-padding-x)"
          >
            <SectionHeader
              divider
              mainCta={
                <Group justify="right">
                  <Button
                    onClick={async () => {
                      await queryClient.invalidateQueries([
                        "listTraceabilityRequests",
                      ])
                      submitHandlers.open()
                    }}
                    icon={<IconArrowBigUpLinesFilled />}
                    size="md"
                  >
                    {t("request.actions.batchSubmit")}
                  </Button>
                </Group>
              }
            />
            <Stack gap="xs">
              <Group justify="space-between">
                <RequestFilters
                  filters={filters}
                  setFilters={setFilters}
                  sorts={[
                    {
                      value: orderDateSort.value,
                      iconNext: orderDateSort.iconNext,
                      name: t("item.date"),
                      toggle: toggleOrderDateSort,
                      hide: !isSortableByOrderDate,
                    },
                    {
                      value: dueDateSort.value,
                      iconNext: dueDateSort.iconNext,
                      name: t("item.marketLaunchDate"),
                      toggle: toggleDueDateSort,
                      hide: !isSortableByDueDate,
                    },
                    {
                      value: receptionDateSort.value,
                      iconNext: receptionDateSort.iconNext,
                      name: t("request.labels.requestDate"),
                      toggle: toggleReceptionDateSort,
                    },
                    {
                      value: referenceSort.value,
                      iconNext: referenceSort.iconNext,
                      name: t("item.reference"),
                      toggle: toggleReferenceSort,
                    },
                    {
                      value: clientSort.value,
                      iconNext: clientSort.iconNext,
                      name: t("item.client"),
                      toggle: toggleClientSort,
                    },
                  ]}
                />
                <Pagination
                  size="sm"
                  value={pagination.page + 1}
                  onChange={(page) =>
                    setPagination((prev) => ({ ...prev, page: page - 1 }))
                  }
                  total={Math.ceil((data?.total ?? 0) / REQUESTS_PER_PAGE)}
                />
              </Group>
            </Stack>
          </Stack>
          {isRequestsLoading ? (
            <Loader fullPage />
          ) : requests.length ? (
            <Stack gap="xl">
              {requests.map((request) => (
                <Card key={request.original.id} shadow="xs">
                  <TraceabilityRequest
                    productionSites={productionSites}
                    initialData={request}
                    operationChains={operationChains}
                    status={status}
                  />
                </Card>
              ))}
            </Stack>
          ) : (
            <Center pt="xl">
              <Title order={3}>
                {status === "OPENED"
                  ? t("requests.noRequests.open")
                  : t("requests.noRequests.closed")}
              </Title>
            </Center>
          )}
          {status == "OPENED" && (
            <>
              <BatchModal />
              <SubmitRequestsModal
                operationChains={operationChains}
                opened={openedSubmit}
                onClose={submitHandlers.close}
              />
            </>
          )}
        </Stack>
      </BatchUpdateModalProvider>
    )
  }
)
